/**
 * Polymarket Safe 地址派生 Hook
 *
 * 功能说明：
 * - Polymarket 使用 Safe 代理钱包存储用户资金和持仓
 * - Safe 地址通过用户 EOA 地址确定性派生（相同 EOA 始终产生相同 Safe）
 * - Safe 地址是用户在 Polymarket 上的实际资产地址
 *
 * 使用场景：
 * - 查询用户在 Polymarket 的余额
 * - 查询用户的持仓（Positions）
 * - 提交交易订单
 *
 * 地址关系：
 * EOA 地址 → 用于签名交易
 * Safe 地址 → 用于存储资金和持仓
 */
'use client';

import { useMemo, useState, useEffect, useCallback } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { deriveSafe } from '@/lib/predict-relayer';
import { useUserStore } from '@/app/stores/useUserStore';
import { createPublicClient, http } from 'viem';
import { polygon } from 'viem/chains';
import { CHAIN_RPC_URLS } from '@/lib/rpc-utils';

/** Polygon RPC URL */
const POLYGON_RPC_URL = process.env.NEXT_PUBLIC_POLYGON_RPC_URL || CHAIN_RPC_URLS[137];

// ============ Viem 客户端 ============

/**
 * 创建 Polygon 公共客户端
 * 用于检查 Safe 合约的部署状态
 */
const publicClient = createPublicClient({
  chain: polygon,
  transport: http(POLYGON_RPC_URL),
});

// ============ Hook 返回类型 ============

export interface UseSafeAddressReturn {
  /** 用户的 EOA 地址（用于签名） */
  eoaAddress: string | null;
  /** Polymarket Safe 代理钱包地址（用于资金和持仓） */
  safeAddress: string | null;
  /** Safe 合约是否已部署到链上 */
  isDeployed: boolean | null;
  /** 是否正在检查部署状态 */
  isChecking: boolean;
  /** 重新检查部署状态的函数 */
  recheckDeployment: () => Promise<void>;
  /** 是否准备就绪（已登录且有 Safe 地址） */
  isReady: boolean;
}

// ============ Hook 实现 ============

/**
 * 获取用户的 Polymarket Safe 代理钱包地址
 *
 * @returns {UseSafeAddressReturn} Safe 地址相关状态和方法
 *
 * @example
 * ```tsx
 * const { eoaAddress, safeAddress, isDeployed, isReady } = useSafeAddress();
 *
 * if (isReady) {
 *   console.log('EOA:', eoaAddress);   // 用于签名
 *   console.log('Safe:', safeAddress); // 用于查询余额/持仓
 * }
 * ```
 */
export function useSafeAddress(): UseSafeAddressReturn {
  const { authenticated } = usePrivy();
  const { evmAddress } = useUserStore();

  // 部署状态
  const [isDeployed, setIsDeployed] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  // ============ 派生 Safe 地址 ============

  /**
   * 从 EOA 地址派生 Safe 地址
   * Safe 地址是确定性的 - 相同的 EOA 总是产生相同的 Safe
   */
  const safeAddress = useMemo(() => {
    if (!evmAddress) return null;

    try {
      return deriveSafe(evmAddress);
    } catch (error) {
      console.error('[useSafeAddress] 派生 Safe 地址失败:', error);
      return null;
    }
  }, [evmAddress]);

  // ============ 检查部署状态 ============

  /**
   * 检查 Safe 合约是否已部署到链上
   * 如果有代码存在，说明合约已部署
   */
  const checkDeployment = useCallback(async () => {
    if (!safeAddress) {
      setIsDeployed(null);
      return;
    }

    setIsChecking(true);
    try {
      // 获取合约代码
      const code = await publicClient.getCode({
        address: safeAddress as `0x${string}`,
      });
      // 如果有代码（不是空或 0x），说明合约已部署
      setIsDeployed(code !== undefined && code !== '0x');
    } catch (error) {
      console.error('[useSafeAddress] 检查 Safe 部署状态失败:', error);
      setIsDeployed(null);
    } finally {
      setIsChecking(false);
    }
  }, [safeAddress]);

  // ============ 初始化检查 ============

  /**
   * Safe 地址变化时自动检查部署状态
   */
  useEffect(() => {
    checkDeployment();
  }, [checkDeployment]);

  // ============ 返回值 ============

  return {
    eoaAddress: evmAddress,
    safeAddress,
    isDeployed,
    isChecking,
    recheckDeployment: checkDeployment,
    isReady: authenticated && !!safeAddress,
  };
}
